import type { ClockPort, TimerHandle } from '@lw/shared/clock';
import {
  BURST_COUNT,
  BURST_SPACING_MS,
  DRIFT_PPM,
  HB_INTERVAL_MS,
  PONG_LOST_MS,
  SLEEP_DIVERGENCE_MS,
  WIN_CONN_MS,
} from '@lw/shared/constants';
import { OffsetEstimator, type EstimatorEvent } from '@lw/shared/time-sync';

/** Tolerance of the passive check for handler scheduling delay (foreground tabs). */
const PASSIVE_SLACK_MS = 50;

export interface QBlock {
  u: number | null;
  rttBest: number | null;
  loss30: number;
  state: 'RED' | 'ORANGE' | 'GREEN';
  hidden: boolean;
  reconnects300: number;
}

export interface PingFrame {
  type: 'ping';
  seq: number;
  t0: number;
  q: QBlock;
}

export interface PongFrame {
  type: 'pong';
  seq: number;
  t0: number;
  t1: number;
  t2: number;
  epochId: string;
}

export interface TimeCache {
  aWall: number;
  u: number | null;
  savedAtWall: number;
}

export interface TimeCacheStore {
  load(): TimeCache | null;
  save(c: TimeCache): void;
}

export type ChronoEvent =
  | EstimatorEvent
  | { type: 'CLOCK_FREEZE_DETECTED'; dPerf: number; dWall: number; provisionalPauseMs: number }
  | { type: 'PAUSE_CONFIRMED'; pauseMs: number; e: number }
  | { type: 'WALL_STEP_DETECTED'; deltaMs: number; e: number }
  | { type: 'DEGRADED_RESTORED'; e: number }
  | { type: 'EPOCH_CHANGED'; from: string | null; to: string }
  | { type: 'SYNC_SUSPECT'; deltaMs: number };

export type ChronoMode = 'NORMAL' | 'DEGRADED_WALL' | 'NONE';

export interface ChronoOptions {
  /** sends a ping frame; throws or silently drops when the link is down */
  send: (frame: PingFrame) => void;
  /**
   * Optional request/response sampler (GET /api/v1/time) used for bursts while no socket is
   * attached, so a FIRE posted over plain HTTPS still carries a fresh rttBestMs (§8.1).
   */
  httpPing?: (frame: PingFrame) => Promise<PongFrame>;
  onEvent?: (e: ChronoEvent) => void;
  store?: TimeCacheStore;
  /** indicator state and window facts the kernel does not own */
  qExtra?: () => { state: QBlock['state']; hidden: boolean; reconnects300: number };
  tickMs?: number;
}

interface Outstanding {
  seq: number;
  t0: number;
  wallAtT0: number;
}

/**
 * The client's clock (ARCHITECTURE §5.3–5.5, §8.6): NTP-style sampling over the heartbeat, the
 * min-RTT anchor with its uncertainty bound, sleep-divergence detection with the wall-anchor
 * fallback, and the time_cache that lets a refreshed-while-offline page paint a countdown.
 * Headless and framework-free; all time through the ClockPort.
 */
export class ChronoClient {
  readonly est = new OffsetEstimator();
  private readonly clock: ClockPort;
  private readonly opts: ChronoOptions;
  private readonly tickMs: number;

  private attached = false;
  private running = false;
  private tickHandle: TimerHandle | null = null;
  private burstHandle: TimerHandle | null = null;

  private seq = 0;
  private outstanding = new Map<number, Outstanding>();
  private lastPingSentPerf: number | null = null;
  private lastPongPerf: number | null = null;
  private pongsPerf: number[] = [];
  private lostPerf: number[] = [];
  private rtts: { t: number; rtt: number }[] = [];

  private epochId: string | null = null;
  private lastTick: { perf: number; wall: number } | null = null;
  private mode: ChronoMode = 'NONE';
  private wallAnchorCached: number | null = null;
  private lowConfidence = false;

  /** Pause accounting for the outbox (§5.5): confirmed + provisional monotonic pause, ms. */
  private pauseConfirmedMs = 0;
  private suspect: { dPerf: number; dWall: number; aOld: number | null; provisional: number } | null = null;
  private lastSaveWall = Number.NEGATIVE_INFINITY;

  constructor(clock: ClockPort, opts: ChronoOptions) {
    this.clock = clock;
    this.opts = opts;
    this.tickMs = opts.tickMs ?? 250;
    const cached = opts.store?.load() ?? null;
    if (cached) {
      this.wallAnchorCached = cached.aWall;
      this.est.restoreWallAnchor(cached.aWall);
      this.mode = 'DEGRADED_WALL';
      this.lowConfidence = true;
    }
  }

  // ── lifecycle ────────────────────────────────────────────────────────────────────────────────

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTick = { perf: this.clock.perfNow(), wall: this.clock.wallNow() };
    this.scheduleTick();
  }

  stop(): void {
    this.running = false;
    if (this.tickHandle) this.clock.clearTimeout(this.tickHandle);
    if (this.burstHandle) this.clock.clearTimeout(this.burstHandle);
    this.tickHandle = null;
    this.burstHandle = null;
  }

  /** Transport is open: start the heartbeat and burst (a burst already in progress continues). */
  onOpen(): void {
    this.attached = true;
    if (!this.burstHandle) this.burst();
  }

  onClose(): void {
    this.attached = false;
    this.outstanding.clear();
  }

  /** visibilitychange→visible, pageshow, resume, online: divergence check then burst (§5.3, §5.5). */
  onResume(): void {
    this.checkDivergence();
    this.burst();
  }

  /**
   * A hidden tab pings on any inbound frame when ≥ 1 s since the last ping (§5.3). The passive check
   * is skipped while hidden: browsers deliver messages to background tabs tens to hundreds of ms
   * late, which looks exactly like a wrong anchor (seen on the live spike).
   */
  onServerFrame(srvEpoch?: number): void {
    const now = this.clock.perfNow();
    const hidden = this.opts.qExtra?.().hidden ?? false;
    if (srvEpoch !== undefined && !hidden) this.passiveCheck(srvEpoch);
    if (this.attached && (this.lastPingSentPerf === null || now - this.lastPingSentPerf >= HB_INTERVAL_MS)) this.ping();
  }

  // ── sampling ─────────────────────────────────────────────────────────────────────────────────

  private sampleWaiters: { need: number; resolve: () => void; timer: TimerHandle }[] = [];

  /**
   * The pre-flush mini-burst (§8.5): n pings sent back-to-back (no timers — background tabs throttle
   * them to 1 s), resolved from the pong itself. Resolves when `n` fresh samples arrived, or one fresh
   * sample has arrived and `softMs` passed, or `timeoutMs` elapsed.
   */
  burstAndWait(n: number, timeoutMs: number, softMs = 300): Promise<void> {
    const before = this.est.acceptedCount();
    if (this.attached) {
      for (let i = 0; i < n; i++) this.ping();
    } else if (this.opts.httpPing) {
      void this.httpBurst(n);
    }
    return new Promise((resolve) => {
      const start = this.clock.perfNow();
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        this.clock.clearTimeout(w.timer);
        this.sampleWaiters = this.sampleWaiters.filter((x) => x !== w);
        resolve();
      };
      const check = (): void => {
        const fresh = this.est.acceptedCount() - before;
        if (fresh >= n || (fresh >= 1 && this.clock.perfNow() - start >= softMs)) finish();
      };
      const w = { need: n, resolve: check, timer: this.clock.setTimeout(finish, timeoutMs) };
      this.sampleWaiters.push(w);
      // a soft deadline re-check for the "one sample is enough" rule
      this.clock.setTimeout(check, softMs);
      check();
    });
  }

  /** Send ≥ n pings at 250 ms spacing (default the 4-ping burst). Without a socket, over HTTP if configured. */
  burst(n: number = BURST_COUNT): void {
    if (!this.attached) {
      if (this.opts.httpPing) void this.httpBurst(n);
      return;
    }
    if (this.burstHandle) this.clock.clearTimeout(this.burstHandle);
    let left = n;
    const fire = (): void => {
      if (!this.attached || left <= 0) {
        this.burstHandle = null;
        return;
      }
      this.ping();
      left -= 1;
      this.burstHandle = left > 0 ? this.clock.setTimeout(fire, BURST_SPACING_MS) : null;
    };
    fire();
  }

  private async httpBurst(n: number): Promise<void> {
    for (let i = 0; i < n; i++) {
      const t0 = this.clock.perfNow();
      const seq = ++this.seq;
      this.outstanding.set(seq, { seq, t0, wallAtT0: this.clock.wallNow() });
      this.lastPingSentPerf = t0;
      try {
        const pong = await (this.opts.httpPing as NonNullable<ChronoOptions['httpPing']>)({ type: 'ping', seq, t0, q: this.qBlock() });
        this.onPong(pong);
      } catch {
        this.outstanding.delete(seq);
      }
    }
  }

  ping(): void {
    if (!this.attached) return;
    const t0 = this.clock.perfNow();
    const seq = ++this.seq;
    this.outstanding.set(seq, { seq, t0, wallAtT0: this.clock.wallNow() });
    this.lastPingSentPerf = t0;
    try {
      this.opts.send({ type: 'ping', seq, t0, q: this.qBlock() });
    } catch {
      // a closed socket is reported by the transport; the ping is simply lost
    }
  }

  onPong(p: PongFrame): void {
    const t3 = this.clock.perfNow();
    const wallAtT3 = this.clock.wallNow();
    const o = this.outstanding.get(p.seq);
    if (!o) return; // unknown or already expired
    this.outstanding.delete(p.seq);
    this.lastPongPerf = t3;
    this.pongsPerf.push(t3);

    if (this.epochId !== null && p.epochId !== this.epochId) {
      this.emit(this.est.invalidate('EPOCH_CHANGED'));
      this.emit({ type: 'EPOCH_CHANGED', from: this.epochId, to: p.epochId });
      this.outstanding.clear();
      this.epochId = p.epochId;
      this.burst();
    }
    this.epochId = p.epochId;

    const res = this.est.addSample({ t0: o.t0, t1: p.t1, t2: p.t2, t3 }, wallAtT3);
    if (!res.accepted) {
      for (const e of res.events) this.emit(e);
      return;
    }
    this.rtts.push({ t: t3, rtt: res.sample.rtt });
    for (const e of res.events) this.emit(e);
    for (const w of [...this.sampleWaiters]) w.resolve();

    // First fresh sample after a suspected pause: classify and restore the mono anchor (§5.5).
    const fresh = t3 + (this.est.anchor() as number);
    if (this.suspect) {
      const s = this.suspect;
      const wallEstimate = this.wallAnchorCached !== null ? wallAtT3 + this.wallAnchorCached : null;
      const e = wallEstimate !== null ? wallEstimate - fresh : 0;
      if (s.aOld !== null) {
        const monoErr = t3 + s.aOld - fresh; // negative ≈ −pause when the monotonic clock really paused
        const divergence = s.dWall - s.dPerf;
        if (Math.abs(monoErr) < Math.abs(divergence) / 2) {
          this.emit({ type: 'WALL_STEP_DETECTED', deltaMs: divergence, e });
        } else {
          this.pauseConfirmedMs += Math.max(0, -monoErr);
          this.emit({ type: 'PAUSE_CONFIRMED', pauseMs: Math.max(0, -monoErr), e });
        }
      } else {
        // no mono anchor to compare against: trust the detector
        this.pauseConfirmedMs += s.provisional;
        this.emit({ type: 'PAUSE_CONFIRMED', pauseMs: s.provisional, e });
      }
      this.suspect = null;
    }
    if (this.mode !== 'NORMAL') {
      // the first fresh sample restores the mono anchor; the log records the wall anchor's error e
      const wallEstimate = this.wallAnchorCached !== null ? wallAtT3 + this.wallAnchorCached : null;
      if (this.mode === 'DEGRADED_WALL' && wallEstimate !== null) this.emit({ type: 'DEGRADED_RESTORED', e: wallEstimate - fresh });
      this.mode = 'NORMAL';
      this.lowConfidence = false;
    }
    this.wallAnchorCached = this.est.wallAnchorValue();
    this.persist(wallAtT3);
  }

  // ── queries ──────────────────────────────────────────────────────────────────────────────────

  /** Server time now, or null when this device has never synced (digits blank, §5.5). */
  serverNow(): number | null {
    const perf = this.clock.perfNow();
    if (this.mode === 'NORMAL') {
      const a = this.est.anchor();
      if (a !== null) return perf + a;
    }
    if (this.wallAnchorCached !== null) return this.clock.wallNow() + this.wallAnchorCached;
    return null;
  }

  currentMode(): ChronoMode {
    return this.mode;
  }

  isLowConfidence(): boolean {
    return this.lowConfidence;
  }

  /** U_self; null while sync is INVALID. */
  uncertainty(): number | null {
    return this.mode === 'NORMAL' ? this.est.uncertainty(this.clock.perfNow()) : null;
  }

  isValid(): boolean {
    return this.mode === 'NORMAL' && this.est.isValid(this.clock.perfNow());
  }

  rttBest(): number | null {
    return this.est.best()?.rtt ?? null;
  }

  currentEpochId(): string | null {
    return this.epochId;
  }

  samplesAccepted(): number {
    return this.est.acceptedCount();
  }

  /** Total monotonic pause observed so far (confirmed + unresolved provisional), for the envelope. */
  pauseTotalMs(): number {
    return this.pauseConfirmedMs + (this.suspect?.provisional ?? 0);
  }

  /** Connection window facts for the indicator (§7.1). */
  metrics(): { sincePongMs: number | null; lost30: number; rttMin30: number | null; rttP90_30: number | null; loss30: number } {
    const now = this.clock.perfNow();
    this.pruneWindows(now);
    const rtts = this.rtts.map((r) => r.rtt).sort((a, b) => a - b);
    const sent = this.pongsPerf.length + this.lost30Count();
    return {
      sincePongMs: this.lastPongPerf === null ? null : now - this.lastPongPerf,
      lost30: this.lost30Count(),
      rttMin30: rtts.length ? (rtts[0] as number) : null,
      rttP90_30: rtts.length ? (rtts[Math.min(rtts.length - 1, Math.floor(0.9 * rtts.length))] as number) : null,
      loss30: sent === 0 ? 0 : this.lost30Count() / sent,
    };
  }

  // ── internals ────────────────────────────────────────────────────────────────────────────────

  private qBlock(): QBlock {
    const m = this.metrics();
    const x = this.opts.qExtra?.() ?? { state: 'RED' as const, hidden: false, reconnects300: 0 };
    return { u: this.uncertainty(), rttBest: this.rttBest(), loss30: m.loss30, state: x.state, hidden: x.hidden, reconnects300: x.reconnects300 };
  }

  private scheduleTick(): void {
    if (!this.running) return;
    this.tickHandle = this.clock.setTimeout(() => this.tick(), this.tickMs);
  }

  private tick(): void {
    const now = this.clock.perfNow();
    this.checkDivergence();
    // expire unanswered pings
    for (const [seq, o] of this.outstanding) {
      if (now - o.t0 > PONG_LOST_MS) {
        this.outstanding.delete(seq);
        this.lostPerf.push(now);
      }
    }
    if (this.attached && !this.burstHandle && (this.lastPingSentPerf === null || now - this.lastPingSentPerf >= HB_INTERVAL_MS)) this.ping();
    this.scheduleTick();
  }

  /** §5.5 divergence detector. */
  private checkDivergence(): void {
    const perf = this.clock.perfNow();
    const wall = this.clock.wallNow();
    const last = this.lastTick;
    this.lastTick = { perf, wall };
    if (!last) return;
    const dPerf = perf - last.perf;
    const dWall = wall - last.wall;
    if (Math.abs(dPerf - dWall) <= SLEEP_DIVERGENCE_MS) return;
    const provisional = Math.max(0, dWall - dPerf);
    const aOld = this.est.anchor();
    if (this.suspect) {
      this.suspect = { dPerf: this.suspect.dPerf + dPerf, dWall: this.suspect.dWall + dWall, aOld: this.suspect.aOld, provisional: this.suspect.provisional + provisional };
    } else {
      this.suspect = { dPerf, dWall, aOld, provisional };
    }
    this.emit(this.est.invalidate('CLOCK_FREEZE_DETECTED'));
    this.outstanding.clear();
    this.mode = this.wallAnchorCached !== null ? 'DEGRADED_WALL' : 'NONE';
    this.emit({ type: 'CLOCK_FREEZE_DETECTED', dPerf, dWall, provisionalPauseMs: provisional });
    this.burst();
  }

  /**
   * §5.4 passive check. A broadcast stamped at server time S reaches us at S + downlink, so
   * `srvEpoch − serverNow()` is legitimately negative by up to one way (bounded by rttBest) and can
   * exceed +U only if our anchor is wrong: valid ⇔ d ∈ [−(rttBest + U), +U].
   */
  private passiveCheck(srvEpoch: number): void {
    const now = this.serverNow();
    const u = this.uncertainty();
    const rtt = this.rttBest();
    if (now === null || u === null || rtt === null) return;
    const d = srvEpoch - now;
    // PASSIVE_SLACK_MS absorbs event-loop scheduling between frame arrival and this handler
    if (d < -(rtt + u + PASSIVE_SLACK_MS) || d > u + PASSIVE_SLACK_MS) {
      this.emit({ type: 'SYNC_SUSPECT', deltaMs: d });
      this.burst();
    }
  }

  private pruneWindows(now: number): void {
    this.pongsPerf = this.pongsPerf.filter((t) => now - t <= WIN_CONN_MS);
    this.lostPerf = this.lostPerf.filter((t) => now - t <= WIN_CONN_MS);
    this.rtts = this.rtts.filter((r) => now - r.t <= WIN_CONN_MS);
  }

  private lost30Count(): number {
    return this.lostPerf.length;
  }

  private persist(wallNow: number): void {
    if (!this.opts.store || this.wallAnchorCached === null) return;
    if (wallNow - this.lastSaveWall < 1000) return;
    this.lastSaveWall = wallNow;
    this.opts.store.save({ aWall: this.wallAnchorCached, u: this.uncertainty(), savedAtWall: wallNow });
  }

  private emit(e: ChronoEvent): void {
    this.opts.onEvent?.(e);
  }
}

/** Drift of a cached wall anchor over `ageMs` (for the LOW_CONFIDENCE label). */
export function cachedAnchorUncertainty(cachedU: number | null, ageMs: number): number {
  return (cachedU ?? 0) + DRIFT_PPM * 1e-6 * ageMs;
}
